/**
 * Reusable contact form field components for RMC order intake
 * These components can be used in both ContactSection and RmcOrderList
 *
 */
import {useOrderProcessFormContext} from '@hconnect/tuc-processes/form/useOrderProcessFormContext'
import {PhoneNumber} from '@hconnect/tuc-processes/types'
import {BorderedTextField, PhoneNumberTextField} from '@hconnect/uikit'
import {TextField, Typography, Box} from '@mui/material'
import React from 'react'
import {Controller, useFormState} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {getTranslatedErrorMessage} from '../../../../../orderIntake/utils/errorTranslation'
import {
  parsePhoneNumber,
  phoneNumberToString
} from '../../../../../orderIntake/utils/phoneNumberUtils'

// Helper function to determine if field has error
const hasFieldError = (error: unknown): boolean => {
  return !!error
}

/**
 * Props for reusable contact name field
 */
interface ContactNameFieldProps {
  fieldName: 'orderPlacer.name' | 'siteContact.name'
  label: string
  required?: boolean
  bordered?: boolean
  testId?: string
  width?: string | number
}

/**
 * Reusable contact name field component
 */
export const ContactNameField: React.FC<ContactNameFieldProps> = ({
  fieldName,
  label,
  required = true,
  bordered = true,
  testId,
  width = '100%'
}) => {
  const {control, setValue} = useOrderProcessFormContext()
  const formState = useFormState({control})
  const {t} = useTranslation()

  const error =
    fieldName === 'orderPlacer.name'
      ? formState.errors?.orderPlacer?.name
      : formState.errors?.siteContact?.name

  const handleNameChange = (newName: string) => {
    setValue(fieldName, newName, {shouldValidate: true})
  }

  if (bordered) {
    return (
      <Box sx={{width}}>
        <Controller
          control={control}
          name={fieldName}
          render={({field}) => (
            <BorderedTextField
              required={required}
              name={field.name}
              value={field.value ?? ''}
              label={label}
              inputProps={{'data-test-id': testId}}
              onChange={(e) => handleNameChange(e.target.value)}
              onKeyDown={(e) => e.code === 'Enter' && e.stopPropagation()}
              error={hasFieldError(error)}
              helperText={getTranslatedErrorMessage(error, t)}
              FormHelperTextProps={{
                style: {
                  color: hasFieldError(error) ? '#d32f2f' : 'inherit',
                  fontWeight: hasFieldError(error) ? 500 : 'normal'
                }
              }}
              fullWidth
            />
          )}
        />
      </Box>
    )
  }

  return (
    <Box sx={{width}}>
      <Controller
        control={control}
        name={fieldName}
        render={({field}) => (
          <TextField
            required={required}
            name={field.name}
            value={field.value}
            label={label}
            variant="outlined"
            inputProps={{'data-test-id': testId}}
            onChange={(e) => handleNameChange(e.target.value)}
            onKeyDown={(e) => e.code === 'Enter' && e.stopPropagation()}
            error={hasFieldError(error)}
            helperText={getTranslatedErrorMessage(error, t)}
            fullWidth
            sx={{
              '& .MuiInputLabel-root': {
                fontSize: '16px',
                color: '#54708c'
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                '& fieldset': {
                  borderColor: hasFieldError(error) ? '#d32f2f' : '#d1d9e0'
                }
              },
              '& .MuiFormHelperText-root': {
                color: hasFieldError(error) ? '#d32f2f' : 'inherit',
                fontWeight: hasFieldError(error) ? 500 : 'normal'
              }
            }}
          />
        )}
      />
      {required && (
        <Typography
          sx={{
            fontSize: '12px',
            color: '#52667a',
            textAlign: 'left',
            marginTop: '4px'
          }}
        >
          {t('orderIntake.rmc.steps.shared.required', 'Required')}
        </Typography>
      )}
    </Box>
  )
}

/**
 * Props for reusable contact phone field
 */
interface ContactPhoneFieldProps {
  fieldName: 'orderPlacer.phoneNumber' | 'siteContact.phoneNumber'
  label: string
  countryCode?: string
  required?: boolean
  bordered?: boolean
  testId?: string
  width?: string | number
}

/**
 * Reusable contact phone field component
 */
export const ContactPhoneField: React.FC<ContactPhoneFieldProps> = ({
  fieldName,
  label,
  countryCode = 'US',
  required = true,
  bordered = true,
  testId,
  width = '100%'
}) => {
  const {control, setValue, watch} = useOrderProcessFormContext()
  const formState = useFormState({control})
  const {t} = useTranslation()

  const error =
    fieldName === 'orderPlacer.phoneNumber'
      ? formState.errors?.orderPlacer?.phoneNumber
      : formState.errors?.siteContact?.phoneNumber

  const phoneNumberCurrentValue = watch(fieldName)

  const handlePhoneChangeForBordered = (
    fullPhoneNumber: string,
    isValid: boolean,
    countryData: {iso2?: string} | undefined,
    currentFieldValue: PhoneNumber | undefined
  ) => {
    // Don't update if we only have a dialing code (happens during initialization)
    // This prevents overwriting a valid prefilled phone number with empty string
    const phoneNumberObj = parsePhoneNumber(fullPhoneNumber, countryData?.iso2 || countryCode)

    // Only update if we have an actual number, not just the dialing code
    const hasActualNumber = phoneNumberObj.number && phoneNumberObj.number.length > 0
    const currentValue = phoneNumberToString(currentFieldValue)

    // Don't replace a valid number with just a dialing code
    if (
      !hasActualNumber &&
      currentValue &&
      currentValue.length > (phoneNumberObj.dialingCode?.length || 0)
    ) {
      return
    }

    setValue(fieldName, phoneNumberObj, {shouldValidate: true})
  }

  if (bordered) {
    return (
      <Box sx={{width}}>
        <Controller
          control={control}
          name={fieldName}
          render={({field}) => (
            <PhoneNumberTextField
              bordered
              id={field.name}
              fullWidth
              ref={field.ref}
              name={field.name}
              data-test-id={testId}
              value={
                phoneNumberToString(field.value) ||
                phoneNumberToString(phoneNumberCurrentValue) ||
                ''
              }
              onPhoneChange={(fullPhoneNumber, isValid, countryData) =>
                handlePhoneChangeForBordered(fullPhoneNumber, isValid, countryData, field.value)
              }
              onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
              options={{
                initialCountry: field.value?.country || countryCode || 'US',
                nationalMode: false,
                formatOnDisplay: false
              }}
              required={required}
              label={label}
              error={hasFieldError(error)}
              helperText={getTranslatedErrorMessage(error, t)}
              FormHelperTextProps={{
                style: {
                  color: hasFieldError(error) ? '#d32f2f' : 'inherit',
                  fontWeight: hasFieldError(error) ? 500 : 'normal'
                }
              }}
            />
          )}
        />
      </Box>
    )
  }

  return (
    <Box sx={{width}}>
      <Controller
        control={control}
        name={fieldName}
        render={({field}) => {
          const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value
            const phoneNumberObj = parsePhoneNumber(inputValue, countryCode)
            setValue(fieldName, phoneNumberObj, {shouldValidate: true})
          }

          return (
            <TextField
              required={required}
              name={field.name}
              value={
                phoneNumberToString(field.value) ||
                phoneNumberToString(phoneNumberCurrentValue) ||
                ''
              }
              label={label}
              variant="outlined"
              inputProps={{'data-test-id': testId}}
              onChange={handlePhoneChange}
              onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
              error={hasFieldError(error)}
              helperText={getTranslatedErrorMessage(error, t)}
              fullWidth
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: '16px',
                  color: '#54708c'
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: hasFieldError(error) ? '#d32f2f' : '#d1d9e0'
                  }
                },
                '& .MuiFormHelperText-root': {
                  color: hasFieldError(error) ? '#d32f2f' : 'inherit',
                  fontWeight: hasFieldError(error) ? 500 : 'normal'
                }
              }}
            />
          )
        }}
      />
      {required && (
        <Typography
          sx={{
            fontSize: '12px',
            color: '#52667a',
            textAlign: 'right',
            marginTop: '4px'
          }}
        >
          {t('orderIntake.rmc.steps.shared.required', 'Required')}
        </Typography>
      )}
    </Box>
  )
}
